import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

// Muestra una lista ya filtrada de a tramos de `pageSize` y suma el siguiente al llegar al final del scroll.
// Mismo mecanismo que Users y OrderProduct: un sentinel al pie del contenedor observado con IntersectionObserver.
// El contenedor con scroll lleva `scrollContainerRef` y el sentinel `sentinelRef` (renderizarlo solo si `hasMore`).
// Al cambiar `resetKey` (por ejemplo, el término de búsqueda) vuelve al primer tramo.
export default function useIncrementalList(items, pageSize, resetKey) {
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const scrollContainerRef = useRef(null);
  const sentinelRef = useRef(null);
  const lastChunkLoadAtRef = useRef(0);

  const total = items.length;
  const visibleItems = useMemo(() => items.slice(0, visibleCount), [items, visibleCount]);
  const hasMore = visibleCount < total;

  const loadNextChunk = useCallback(() => {
    setVisibleCount((c) => (c >= total ? c : Math.min(c + pageSize, total)));
  }, [total, pageSize]);

  useLayoutEffect(() => {
    setVisibleCount(pageSize);
  }, [resetKey, pageSize]);

  // Si el primer tramo no llega a llenar el contenedor no hay scroll: se carga el siguiente
  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || !hasMore) return;
    if (el.scrollHeight <= el.clientHeight + 2) loadNextChunk();
  }, [hasMore, loadNextChunk, visibleItems.length]);

  useEffect(() => {
    const root = scrollContainerRef.current;
    const target = sentinelRef.current;
    if (!root || !target || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        const now = performance.now();
        if (now - lastChunkLoadAtRef.current < 200) return;
        lastChunkLoadAtRef.current = now;
        loadNextChunk();
      },
      { root, rootMargin: "0px 0px 200px 0px", threshold: 0 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loadNextChunk, visibleItems.length]);

  return { visibleItems, total, hasMore, scrollContainerRef, sentinelRef };
}
